import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { get } from 'lodash';
import CCEvents, { EventNames } from '@companycam/companycam-events';

import { useModalToast } from 'components/shared/DesignSystem/ModalToast/useModalToast';
import useCreateReportFromTemplate from './gql/useCreateReportFromTemplate';
import { translate } from 'translations/i18n';

const Container = styled.div`
  height: 100%;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
`;

const Header = styled.div`
  padding: var(--cc_size_spacing_xl) var(--cc_size_spacing_xl)
    var(--cc_size_spacing_l) var(--cc_size_spacing_xl);
  border-bottom: var(--cc_size_border_width_s) solid
    var(--cc_color_border_default);
`;

const Heading = styled.h2`
  font-size: var(--cc_size_text_xl);
  margin: 0;
`;

const Body = styled.div`
  overflow-y: auto;
  padding: 0 0 var(--cc_size_spacing_l);
`;

const List = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0 0 var(--cc_size_spacing_l);
`;

const ListItem = styled.li`
  display: flex;
  align-items: center;
  border-bottom: var(--cc_size_border_width_s) solid
    var(--cc_color_border_default);
  padding: 0;
  transition: background-color 0.2s;
  &:hover {
    background-color: ${(props) => props.highlightColor};
  }
`;

const ListItemPrimary = styled.span`
  flex: 1;
  min-width: 1px;
  display: flex;
`;

const UseButtonHeading = styled.span`
  display: block;
  font-family: 'Averta', system-ui, -apple-system, BlinkMacSystemFont,
    'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-size: var(--cc_size_text_m);
  font-weight: 600;
  color: var(--cc_color_link_secondary);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  position: relative;
`;

const UseButtonDesc = styled.span`
  display: block;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    'Helvetica Neue', Arial, sans-serif;
  font-size: var(--cc_size_text_s);
  color: var(--cc_color_link_subtle);
  margin-block-start: var(--cc_size_spacing_xs);
  line-height: 1.4;

  /* Firefox supports -webkit- prefix for line-clamp */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UseButton = styled.button`
  cursor: pointer;
  text-align: start;
  display: block;
  padding: var(--cc_size_spacing_m) var(--cc_size_spacing_xl);
  flex-grow: 1;
  flex-shrink: 1;
  min-width: 1px;
  transition: transform 0.2s;
  position: relative;
  background: none;
  border: 0;
  &:hover,
  &:focus {
    ${UseButtonHeading}, ${UseButtonDesc} {
      color: var(--cc_color_link_primary);
    }
  }
  &:focus-visible {
    outline: var(--cc_size_border_width_m) solid var(--cc_color_link_primary);
    outline-offset: -1px;
  }
  &:active {
    transform: scale(0.995) translate3d(0, 1px, 0);
  }
`;

const CreateReportFromTemplateContent = ({
  reportTemplates,
  handleScroll,
  projectNodeId,
  assets,
}) => {
  const { showToast } = useModalToast();

  const { createReportFromTemplate } = useCreateReportFromTemplate({
    onSuccess: CCEvents.logEvent(
      EventNames.REPORTS.CREATE_REPORT_FROM_TEMPLATE,
    ),
    onCreateError: () => showToast({ type: 'error' }),
  });

  const onTemplateClick = ({ templateId }) => {
    createReportFromTemplate({
      templateId,
      projectId: projectNodeId,
      assets,
    });
  };

  const renderTemplateItems = (templates) => {
    return templates.map((template) => {
      const id = get(template, 'id', '');
      const title = get(template, 'title', '');
      const description = get(template, 'templateDescription', '');

      return (
        <ListItem
          key={id}
          highlightColor="var(--cc_color_button_background_tint)"
        >
          <ListItemPrimary>
            <UseButton
              type="button"
              onClick={() => onTemplateClick({ templateId: id })}
              className="hint hint--bottom-center"
              aria-label={translate(
                'Reports.templateListModal.useTemplateHint',
              )}
            >
              <UseButtonHeading>{title}</UseButtonHeading>
              {description ? (
                <UseButtonDesc>{description}</UseButtonDesc>
              ) : null}
            </UseButton>
          </ListItemPrimary>
        </ListItem>
      );
    });
  };

  return (
    <Container>
      <Header>
        <Heading>{translate('reports.templateListModal.header')}</Heading>
      </Header>
      <Body onScroll={handleScroll}>
        <List>{renderTemplateItems(reportTemplates)}</List>
      </Body>
    </Container>
  );
};

CreateReportFromTemplateContent.propTypes = {
  reportTemplates: PropTypes.array,
  handleScroll: PropTypes.func,
  projectNodeId: PropTypes.string,
  assets: PropTypes.object,
};

export default CreateReportFromTemplateContent;
